import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LANGUAGES } from "@/i18n";

/* Form furniture for the studio. Nothing here knows what it is editing — the
   panels supply the labels — which is why one bilingual field can serve a hero
   title, a portfolio blurb and an ad's call to action alike. */

export const Panel = ({ title, hint, children, actions }) => (
  <section className="surface rounded-xl p-6" data-testid={`panel-${title}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-[22px] font-semibold leading-tight">{title}</h2>
        {hint ? (
          <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-[color:var(--ink-soft)]">
            {hint}
          </p>
        ) : null}
      </div>
      {actions}
    </div>
    <div className="mt-5">{children}</div>
  </section>
);

export const Field = ({ label, hint, children }) => (
  <div className="mt-4">
    <Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
      {label}
    </Label>
    {hint ? (
      <p className="mt-1 text-[11.5px] text-[color:var(--muted-ink)]">{hint}</p>
    ) : null}
    <div className="mt-1.5">{children}</div>
  </div>
);

export const TextField = ({ label, hint, value, onChange, placeholder = "", testId }) => (
  <Field label={label} hint={hint}>
    <Input
      value={value || ""}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      data-testid={testId}
      className="h-10 text-[13.5px]"
    />
  </Field>
);

export const AreaField = ({ label, hint, value, onChange, rows = 4, placeholder = "", testId }) => (
  <Field label={label} hint={hint}>
    <Textarea
      value={value || ""}
      rows={rows}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      data-testid={testId}
      className="text-[13.5px]"
    />
  </Field>
);

/** One value, two languages, one visible input.

    Two side-by-side boxes for every string would double the height of every
    panel; a tab keeps the form the size of a form. The inactive language is
    never cleared — switching tabs shows it again, unchanged. */
export const BilingualField = ({ label, hint, value, onChange, rows = 0, testId }) => {
  const [lang, setLang] = useState("fr");
  const current = (value || {})[lang] || "";
  const set = (next) => onChange({ ...(value || {}), [lang]: next });

  return (
    <Field
      label={
        <span className="flex items-center gap-2">
          {label}
          <span className="inline-flex gap-0.5 rounded-full p-0.5" style={{ backgroundColor: "var(--paper-2)" }}>
            {Object.keys(LANGUAGES).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => setLang(code)}
                aria-pressed={lang === code}
                data-testid={`${testId}-lang-${code}`}
                className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                style={{
                  backgroundColor: lang === code ? "#ffffff" : "transparent",
                  color: lang === code ? "var(--ink)" : "var(--muted-ink)",
                }}
              >
                {code}
              </button>
            ))}
          </span>
          {/* the other language having no text is worth seeing before publishing */}
          {!((value || {})[lang === "fr" ? "en" : "fr"] || "").trim() ? (
            <span className="text-[10px] normal-case" style={{ color: "var(--warning)" }}>
              {lang === "fr" ? "EN vide" : "FR vide"}
            </span>
          ) : null}
        </span>
      }
      hint={hint}
    >
      {rows ? (
        <Textarea
          value={current}
          rows={rows}
          onChange={(event) => set(event.target.value)}
          data-testid={testId}
          className="text-[13.5px]"
        />
      ) : (
        <Input
          value={current}
          onChange={(event) => set(event.target.value)}
          data-testid={testId}
          className="h-10 text-[13.5px]"
        />
      )}
    </Field>
  );
};

export const Toggle = ({ label, hint, checked, onChange, testId }) => (
  <label
    className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border p-3"
    style={{ borderColor: "var(--line)" }}
  >
    <input
      type="checkbox"
      checked={Boolean(checked)}
      onChange={(event) => onChange(event.target.checked)}
      data-testid={testId}
      className="mt-0.5 h-4 w-4"
    />
    <span>
      <span className="text-[13.5px] font-medium">{label}</span>
      {hint ? (
        <span className="mt-0.5 block text-[11.5px] text-[color:var(--muted-ink)]">{hint}</span>
      ) : null}
    </span>
  </label>
);

/** A row of repeated things — portfolio entries, partners, ad slots — with the
    add and remove controls they all need. */
export const Repeater = ({ items, onChange, blank, renderItem, addLabel, testId }) => (
  <div data-testid={testId}>
    <div className="space-y-4">
      {items.map((item, index) => (
        <div
          key={item.id || index}
          className="rounded-lg border p-4"
          style={{ borderColor: "var(--line)", backgroundColor: "var(--paper)" }}
        >
          <div className="flex items-center justify-between">
            <span className="font-num text-[11px] text-[color:var(--muted-ink)]">
              {String(index + 1).padStart(2, "0")}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => {
                  const next = [...items];
                  [next[index - 1], next[index]] = [next[index], next[index - 1]];
                  onChange(next);
                }}
                className="rounded px-2 py-1 text-[11px] disabled:opacity-30"
                aria-label="Monter"
              >
                ↑
              </button>
              <button
                type="button"
                disabled={index === items.length - 1}
                onClick={() => {
                  const next = [...items];
                  [next[index + 1], next[index]] = [next[index], next[index + 1]];
                  onChange(next);
                }}
                className="rounded px-2 py-1 text-[11px] disabled:opacity-30"
                aria-label="Descendre"
              >
                ↓
              </button>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                data-testid={`${testId}-remove-${index}`}
                className="rounded px-2 py-1 text-[11px]"
                style={{ color: "var(--danger)" }}
              >
                Supprimer
              </button>
            </div>
          </div>
          {renderItem(item, (patch) =>
            onChange(items.map((row, i) => (i === index ? { ...row, ...patch } : row)))
          )}
        </div>
      ))}
    </div>

    <button
      type="button"
      onClick={() => onChange([...items, blank()])}
      data-testid={`${testId}-add`}
      className="mt-4 rounded-full border px-4 py-2 text-[12.5px]"
      style={{ borderColor: "var(--line)" }}
    >
      {addLabel}
    </button>
  </div>
);
